import classNames from 'classnames';
import React, { MouseEvent, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import * as styles from './Tooltip.module.scss';

type PortalProps = {
  children: React.ReactNode;
};

const Portal: React.FC<PortalProps> = ({ children }) => {
  return ReactDOM.createPortal(children, document.body);
};

type Placement = 'top' | 'bottom' | 'left' | 'right';
type Theme = 'light' | 'dark';

export interface TooltipProps {
  TooltipContent: React.ComponentType;
  placement?: Placement;
  testId?: string;
  className?: string;
  theme?: Theme;
  children?: React.ReactNode;
}

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  TooltipContent,
  testId,
  className,
  placement = 'right',
  theme = 'light',
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState<{ top?: number; left?: number }>({});
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const calculateTooltipPosition = (rect: DOMRect) => {
    const { current: tooltip } = tooltipRef;
    const { current: trigger } = triggerRef;

    if (tooltip && trigger) {
      const heightDiff = tooltip.offsetHeight - (trigger.offsetHeight || (trigger.firstChild as any).offsetHeight || 0);
      const widthDiff = tooltip.offsetWidth - (trigger.offsetWidth || (trigger.firstChild as any).offsetWidth || 0);

      switch (placement) {
        case 'right':
          setCoords({
            top: rect.top + window.scrollY - heightDiff / 2 + trigger.offsetHeight / 2,
            left: rect.right + 5,
          });
          break;
        case 'left':
          setCoords({
            top: rect.top + window.scrollY - heightDiff / 2 + trigger.offsetHeight / 2,
            left: rect.left - tooltip.offsetWidth - 5,
          });
          break;
        case 'top':
          setCoords({
            top: rect.top + scrollY - tooltip.offsetHeight - 5,
            left: rect.left - widthDiff / 2,
          });
          break;
        case 'bottom':
          setCoords({
            top: rect.bottom + scrollY + 5,
            left: rect.left - widthDiff / 2,
          });
          break;
      }
    }
  };

  const handleMouseEnter = ({ target }: MouseEvent<HTMLSpanElement>) => {
    const rect = (target as HTMLElement).getBoundingClientRect();
    calculateTooltipPosition(rect);
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  const contentClassNames = classNames(
    styles.Tooltip__Content,
    `Tooltip__Content--${theme}`,
    styles[`Tooltip__Content--${placement}`],
    {
      [styles['Tooltip__Content--visible']]: isVisible,
    },
    className,
  );

  return (
    <span onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <span ref={triggerRef}>{children}</span>
      <Portal>
        <div
          data-test-id={testId}
          ref={tooltipRef}
          className={contentClassNames}
          style={{ left: coords.left, top: coords.top }}
        >
          <TooltipContent />
        </div>
      </Portal>
    </span>
  );
};
